import { WebViewerInstance } from '@pdftron/webviewer';

/**
 * Gets the current page rotation from PDFTron documentViewer.
 * @returns Rotation value (0, 1, 2, 3) representing 0°, 90°, 180°, 270° clockwise
 */
export function getPageRotation(instance: WebViewerInstance, pageNumber: number): number {
  return instance.Core.documentViewer.getCompleteRotation(pageNumber);
}

export interface LabelPosition {
  x: number;
  y: number;
  isInsideAnnotation: boolean;
}

export interface LabelDimensions {
  width: number;
  height: number;
}

export interface AnnotationBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculates label anchor position based on page rotation to keep it at the perceived top-left.
 *
 * Visual top-left of annotation in page coordinates by rotation:
 * - 0°: (x, y)
 * - 90°: (x, y + height)
 * - 180°: (x + width, y + height)
 * - 270°: (x + width, y)
 *
 * @param bounds - The annotation bounds
 * @param labelDimensions - Width and height of the label
 * @param rotation - Page rotation (0, 1, 2, 3)
 * @param labelMargin - Margin between label and annotation edge
 * @param positionInside - If true, position INSIDE at visual top-left (for page labels).
 *                         If false, position OUTSIDE above the annotation (for zone labels).
 */
export function calculateRotatedLabelPosition(
  bounds: AnnotationBounds,
  labelDimensions: LabelDimensions,
  rotation: number,
  labelMargin: number = 0,
  positionInside: boolean = false,
  pageWidth?: number,
  pageHeight?: number,
): LabelPosition {
  const { x, y, width, height } = bounds;
  const { width: labelWidth, height: labelHeight } = labelDimensions;
  const normalizedRotation = ((rotation % 4) + 4) % 4;

  let labelX: number;
  let labelY: number;
  let isInsideAnnotation = positionInside;

  // For page labels: position at visual top-left corner INSIDE the bounds
  if (positionInside) {
    switch (normalizedRotation) {
      case 1: // 90° CW - visual top-left at page (x, y + height)
        labelX = x + labelMargin + labelHeight;
        labelY = y + height;
        break;

      case 2: // 180° - visual top-left at page (x + width, y + height)
        labelX = x + width - labelMargin;
        labelY = y + height - labelMargin;
        break;

      case 3: // 270° CW - visual top-left at page (x + width, y)
        labelX = x + width - labelMargin;
        labelY = y + labelMargin + labelWidth;
        break;

      default: // 0° - visual top-left at page (x, y)
        labelX = x + labelMargin;
        labelY = y + labelMargin;
    }
    return { x: labelX, y: labelY, isInsideAnnotation: true };
  }

  // For zone labels: position OUTSIDE above the annotation
  switch (normalizedRotation) {
    case 1: // 90° CW - visual top-left at page (x, y + height)
      labelX = x - labelMargin;
      labelY = y + height;
      if (labelX - labelHeight < 0) {
        labelX = x + labelHeight;
        isInsideAnnotation = true;
      }
      if (pageHeight !== undefined && labelY > pageHeight) {
        labelY = pageHeight;
      }
      if (labelY - labelWidth < 0) {
        labelY = labelWidth;
      }
      break;

    case 2: // 180° - visual top-left at page (x + width, y + height)
      labelX = x + width;
      labelY = y + height + labelHeight + labelMargin;
      if (pageHeight !== undefined && labelY > pageHeight) {
        labelY = y + height - labelMargin;
        isInsideAnnotation = true;
      }
      if (labelX - labelWidth < 0) {
        labelX = labelWidth;
      }
      break;

    case 3: // 270° CW - visual top-left at page (x + width, y)
      labelX = x + width + labelHeight + labelMargin;
      labelY = y + labelWidth;
      if (pageWidth !== undefined && labelX > pageWidth) {
        labelX = x + width - labelMargin;
        isInsideAnnotation = true;
      }
      if (pageHeight !== undefined && labelY > pageHeight) {
        labelY = pageHeight;
      }
      break;

    default: // 0° - visual top-left at page (x, y)
      labelX = x;
      labelY = y - labelHeight - labelMargin;
      if (labelY < 0) {
        labelY = y;
        isInsideAnnotation = true;
      }
      if (pageWidth !== undefined && labelX + labelWidth > pageWidth) {
        labelX = Math.max(0, pageWidth - labelWidth);
      }
  }

  return { x: labelX, y: labelY, isInsideAnnotation };
}

/**
 * Calculates the bounding box of ONLY the label for a given rotation.
 * Used for repeat annotations where the clickable area is just the label,
 * allowing difference annotations inside the repeat to remain clickable.
 */
export function calculateLabelBounds(
  rectBounds: AnnotationBounds,
  labelDimensions: LabelDimensions,
  rotation: number,
): AnnotationBounds {
  const normX = rectBounds.width < 0 ? rectBounds.x + rectBounds.width : rectBounds.x;
  const normY = rectBounds.height < 0 ? rectBounds.y + rectBounds.height : rectBounds.y;
  const normWidth = Math.abs(rectBounds.width);
  const normHeight = Math.abs(rectBounds.height);

  const normalizedRectBounds = { x: normX, y: normY, width: normWidth, height: normHeight };
  const labelPosition = calculateRotatedLabelPosition(normalizedRectBounds, labelDimensions, rotation);

  const normalizedRotation = ((rotation % 4) + 4) % 4;
  let labelMinX: number, labelMinY: number, labelMaxX: number, labelMaxY: number;

  switch (normalizedRotation) {
    case 1: // 90° and 270° have the same page-space footprint (width/height swapped vs 180°)
    case 3:
      labelMinX = labelPosition.x - labelDimensions.height;
      labelMinY = labelPosition.y - labelDimensions.width;
      labelMaxX = labelPosition.x;
      labelMaxY = labelPosition.y;
      break;
    case 2:
      labelMinX = labelPosition.x - labelDimensions.width;
      labelMinY = labelPosition.y - labelDimensions.height;
      labelMaxX = labelPosition.x;
      labelMaxY = labelPosition.y;
      break;
    default:
      labelMinX = labelPosition.x;
      labelMinY = labelPosition.y;
      labelMaxX = labelPosition.x + labelDimensions.width;
      labelMaxY = labelPosition.y + labelDimensions.height;
  }

  return {
    x: labelMinX,
    y: labelMinY,
    width: labelMaxX - labelMinX,
    height: labelMaxY - labelMinY,
  };
}

/**
 * Draws a label (background + text) that always appears horizontal regardless of page rotation.
 * The label is counter-rotated to remain readable.
 *
 * @param containerBorderWidth - Container stroke width; the label's left edge is nudged out by
 *   half of it to sit flush with the container's visible edge.
 */
export function drawCounterRotatedLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  anchorX: number,
  anchorY: number,
  labelWidth: number,
  labelHeight: number,
  rotation: number,
  fillStyle: string,
  textPadding: number = 4,
  borderRadius: number = 2,
  icon?: CanvasImageSource,
  iconSize: number = 12,
  iconPadding: number = 2,
  font: string = '11px Inter, sans-serif',
  containerBorderWidth: number = 0,
): void {
  const normalizedRotation = ((rotation % 4) + 4) % 4;

  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';

  // Translate to anchor and counter-rotate
  ctx.translate(anchorX, anchorY);
  ctx.rotate((-normalizedRotation * Math.PI) / 2);

  // Calculate drawing offset based on rotation so label extends in correct direction
  let drawX = 0;
  let drawY = 0;

  switch (normalizedRotation) {
    case 1: // 90°
      drawY = -labelHeight;
      break;
    case 3: // 270°
      drawX = -labelWidth;
      break;
    // case 0 and case 2: no offset needed
  }

  const borderOffset = containerBorderWidth / 2;
  drawX -= borderOffset;

  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  ctx.moveTo(drawX + borderRadius, drawY);
  ctx.lineTo(drawX + labelWidth - borderRadius, drawY);
  ctx.arcTo(drawX + labelWidth, drawY, drawX + labelWidth, drawY + borderRadius, borderRadius);
  ctx.lineTo(drawX + labelWidth, drawY + labelHeight);
  ctx.lineTo(drawX, drawY + labelHeight);
  ctx.lineTo(drawX, drawY + borderRadius);
  ctx.arcTo(drawX, drawY, drawX + borderRadius, drawY, borderRadius);
  ctx.closePath();
  ctx.fill();

  // Draw icon if provided
  const iconWidth = icon ? iconSize + iconPadding : 0;
  if (icon) {
    ctx.drawImage(icon, drawX + iconPadding, drawY + (labelHeight - iconSize) / 2, iconSize, iconSize);
  }

  // Draw text
  ctx.font = font;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, drawX + textPadding + iconWidth, drawY + labelHeight / 2);

  ctx.restore();
}
